import {
  AbsenceStatus,
  AbsenceStatusCombobox,
  AbsenceTypeCombobox,
  Button,
  Card,
  Checkbox,
  DatePicker,
  FAIcon,
  Field,
  FieldLabel,
  HideInProduction,
  LeaderGroupCombobox,
  MonthSelect,
  OnlyInProduction,
  UserCombobox,
} from '@/components'
import { cn } from '@/lib/utils'
import type { UseLeaderGroupsSuspenseReturn } from '@/utils'
import {
  PERMISSIONS,
  useAdmin,
  useFormWatch,
  usePermissions,
  useUser,
} from '@/utils'
import { usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { formatISO } from 'date-fns'
import { Activity } from 'react'
import { Controller, type UseFormReturn } from 'react-hook-form'
import z from 'zod'
import { useAbsenceMutations } from '../mutations.hooks'
import type { CreateAbsenceForm } from '../types'
import { AbsenceDialog } from './absence-dialog'
import { CreateAbsenceModal } from './CreateAbsenceModal'

export const AbsencesFiltersSchema = z.object({
  view: z.enum(['table', 'calendar', 'requests']),
  users: z.string().array(),
  absenceType: z.string(),
  requestStatus: z.enum(AbsenceStatus).or(z.literal('')),
  dateRange: z.object({
    from: z.date(),
    to: z.date(),
  }),
  month: z.date(),
  leaderGroup: z.string(),
  salaryMode: z.boolean(),
})

export type AbsencesFiltersForm = z.infer<typeof AbsencesFiltersSchema>

interface Props {
  form: UseFormReturn<AbsencesFiltersForm>
  leaderGroups: UseLeaderGroupsSuspenseReturn
}

export function AbsencesFilters({ form, leaderGroups }: Props) {
  const translations = useTranslate({
    users: 'absences.filters.users',
    absenceType: 'absences.filters.absence-type',
    requestStatus: 'absences.filters.request-status',
    dateRange: 'absences.filters.date-range',
    month: 'absences.filters.month',
    leaderGroup: 'absences.filters.leader-group',
    salaryMode: 'absences.filters.salary-mode',

    registerAbsence: 'absences.absence-dialog.add',
  })

  const isAdmin = useAdmin()
  const me = useUser()

  const view = useFormWatch(form, 'view')
  const leaderGroup = useFormWatch(form, 'leaderGroup')

  const selectedLeaderGroup = leaderGroups.myLeaderGroups.find(
    lg => lg.id === leaderGroup
  )
  const selectedLeaderGroupUsers = new Set(
    selectedLeaderGroup?.users.map(u => u.id) ?? []
  )

  function hideUserItem(item: { id: string }) {
    if (!selectedLeaderGroup) return false
    return !selectedLeaderGroupUsers.has(item.id)
  }

  const { hasPermissions } = usePermissions()
  const canViewOthersAbsence = hasPermissions(
    PERMISSIONS.timesheets.view.othersabsence,
    false
  )
  const canAddAbsence = hasPermissions(
    PERMISSIONS.timesheets.add.absence,
    false
  )
  const canAddOthersAbsence = hasPermissions(
    PERMISSIONS.timesheets.add.othersabsence,
    false
  )
  const canViewSalaryMode = hasPermissions(
    PERMISSIONS.salary.view.salaryMode,
    false
  )

  /* REMOVE BELOW WHEN NEW ABSENCE FLOW IS READY */
  const {
    mutations: { create: createAbsence },
  } = useAbsenceMutations()

  const addPrompt = usePrompt()
  const admin = useAdmin()

  async function handleCreateAbsence() {
    const { data } = await addPrompt<CreateAbsenceForm | null>(resolve => (
      <CreateAbsenceModal admin={admin} onSubmit={resolve} />
    ))

    if (!data) return

    const user = data.user || (!admin ? me.id : null)
    await createAbsence({
      variables: {
        input: {
          reason: data.reason,
          absenceType: data.absenceType ?? '',
          datetimeStart: formatISO(data.dateFrom),
          datetimeEnd: formatISO(data.dateTo),
          user,
        },
      },
    })
  }
  /* REMOVE ABOVE WHEN NEW ABSENCE FLOW IS READY */

  return (
    <Card className={cn('mb-4 p-0')} contentClassName="p-0">
      <div className={cn(canAddAbsence && 'md:grid md:grid-cols-[1fr_auto]')}>
        <form
          className={cn(
            'grid p-4 gap-4',
            'md:flex md:flex-wrap md:justify-start'
          )}
        >
          <Activity mode={leaderGroups.hasAccess ? 'visible' : 'hidden'}>
            <Controller
              control={form.control}
              name="leaderGroup"
              render={({ field }) => (
                <Field>
                  <FieldLabel>{translations.leaderGroup}</FieldLabel>
                  <LeaderGroupCombobox
                    {...field}
                    mode="single"
                    clearable={isAdmin}
                    onChange={value => {
                      field.onChange(value)
                      form.setValue('users', [])
                    }}
                  />
                </Field>
              )}
            />
          </Activity>

          <Activity mode={canViewOthersAbsence ? 'visible' : 'hidden'}>
            <Controller
              control={form.control}
              name="users"
              render={({ field }) => (
                <Field>
                  <FieldLabel>{translations.users}</FieldLabel>
                  <UserCombobox
                    {...field}
                    mode="multiple"
                    hideItem={hideUserItem}
                  />
                </Field>
              )}
            />
          </Activity>

          <Controller
            control={form.control}
            name="absenceType"
            render={({ field }) => (
              <Field>
                <FieldLabel>{translations.absenceType}</FieldLabel>
                <AbsenceTypeCombobox {...field} mode="single" />
              </Field>
            )}
          />

          <Activity mode={view === 'requests' ? 'hidden' : 'visible'}>
            <Controller
              control={form.control}
              name="requestStatus"
              render={({ field }) => (
                <Field>
                  <FieldLabel>{translations.requestStatus}</FieldLabel>
                  <AbsenceStatusCombobox {...field} mode="single" />
                </Field>
              )}
            />
          </Activity>

          <Activity mode={view === 'table' ? 'visible' : 'hidden'}>
            <Controller
              control={form.control}
              name="dateRange"
              render={({ field }) => (
                <Field>
                  <FieldLabel>{translations.dateRange}</FieldLabel>
                  <DatePicker {...field} mode="range" format="PP" />
                </Field>
              )}
            />
          </Activity>

          <Activity mode={view === 'calendar' ? 'visible' : 'hidden'}>
            <Controller
              control={form.control}
              name="month"
              render={({ field }) => (
                <Field>
                  <FieldLabel>{translations.month}</FieldLabel>
                  <MonthSelect {...field} />
                </Field>
              )}
            />
          </Activity>

          <Activity mode={canViewSalaryMode ? 'visible' : 'hidden'}>
            <Controller
              control={form.control}
              name="salaryMode"
              render={({ field }) => (
                <Field className="items-center">
                  <FieldLabel>{translations.salaryMode}</FieldLabel>
                  <Checkbox {...field} />
                </Field>
              )}
            />
          </Activity>
        </form>

        {canAddAbsence && (
          <div
            className={cn(
              'p-4',
              'md:flex md:items-end md:border-l md:border-l-gray-200'
            )}
          >
            <OnlyInProduction>
              <Button className="w-full" onClick={handleCreateAbsence}>
                <FAIcon icon="plus" />
                <span>{translations.registerAbsence}</span>
              </Button>
            </OnlyInProduction>

            <HideInProduction>
              <AbsenceDialog canAddForOthers={canAddOthersAbsence} />
            </HideInProduction>
          </div>
        )}
      </div>
    </Card>
  )
}
